import { shallowEqual, useDispatch } from 'react-redux';
import { useQueryClient } from '@zg-rentals/react-query5';

import { IconPanorama360Outline } from '@zillow/constellation';
import { Box, Icon, Paragraph, TextButton, Toast, useToast } from '@zillow/constellation-10';
import { IconDeleteOutline } from '@zillow/constellation-icons';

import { useSelector } from '../../../selectors/useSelector';

import { useUpdateVideoLink } from '../../../hooks/queries/video';

import PropertiesActions from '../../../actions/PropertiesActions';

import { GET_LISTING_RECS } from '../../../constants/RecommendationsConstants';

import AddVideoLinkModalTrigger from '../../Modal/AddVideoLinkModalTrigger';

import { containerStyles } from './styles';

const VideoFormField = () => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { enqueueToast } = useToast();

  const { videoUrl, listingAlias, selfServeOnboardingStatus, canUpdateListing, isArchived, isUpdateListingRestricted } =
    useSelector(
      (state) => ({
        canUpdateListing: state.user.features.canUpdateListing,
        isArchived: state.properties.currentProperty.isArchived,
        isUpdateListingRestricted: state.properties.currentProperty.restrictions.isUpdateListingRestricted,
        videoUrl: state.properties.currentProperty.videoUrl,
        listingAlias: state.properties.currentProperty.listingAlias,
        selfServeOnboardingStatus: state.properties.currentProperty.selfServeOnboardingStatus,
      }),
      shallowEqual,
    );

  const { mutate: deleteLink } = useUpdateVideoLink(listingAlias);

  const isDisabled =
    !canUpdateListing ||
    isArchived ||
    isUpdateListingRestricted ||
    (selfServeOnboardingStatus.isSelfServeOnboardingInProgress && selfServeOnboardingStatus.isUpdateRestricted);

  const handleDeleteUrl = () => {
    deleteLink('', {
      onSuccess: () => {
        dispatch(PropertiesActions.updateCurrentProperty({ videoUrl: '' }));

        queryClient.invalidateQueries({ queryKey: [GET_LISTING_RECS] });
      },
      onError: () => enqueueToast(<Toast tone="critical">Error occurred when deleting video link.</Toast>),
    });
  };

  if (videoUrl) {
    return (
      <Box css={{ display: 'flex', alignItems: 'center', gap: '200', overflow: 'hidden' }}>
        <Icon
          render={<IconDeleteOutline />}
          data-testid="add-video-delete-button"
          size="sm"
          onClick={handleDeleteUrl}
          css={{ color: 'icon.accent.blue.hero', cursor: 'pointer', minWidth: '400' }}
        />
        <TextButton asChild>
          <a target="_blank" href={videoUrl} rel="noreferrer">
            {videoUrl}
          </a>
        </TextButton>
      </Box>
    );
  }
  return (
    <Box css={containerStyles}>
      <Box css={{ marginBottom: '400' }}>
        <IconPanorama360Outline size="md" />
      </Box>
      <Paragraph css={{ marginBottom: '400' }}>Add a video link</Paragraph>
      <AddVideoLinkModalTrigger isDisabled={isDisabled} />
    </Box>
  );
};

export default VideoFormField;
